import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, X } from "lucide-react";
import { PageHead, Reveal, Eyebrow } from "@/components/Motion";
import { Loading, useApi } from "@/components/Common";
import { api, media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useEffect } from "react";

const Disclaimer = () => <p className="mt-16 text-xs text-[#F6EFE0]/70 border-t border-[#C9A24B]/20 pt-6" data-testid="investment-disclaimer">For general information only. Nothing on this website is investment advice or an offer to buy or sell any investment.</p>;

const ClosedDoor = () => (
  <div className="relative min-h-[100svh] flex items-center justify-center px-6 overflow-hidden" data-testid="enterprise-closed">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#163B29,#0B1D14_65%)]" />
    <motion.div className="relative w-full max-w-lg aspect-[3/4] max-h-[70vh] rounded-t-[200px] border border-[#C9A24B]/60 bg-[#0F2A1D] flex flex-col items-center justify-center text-center p-10 shadow-[0_40px_120px_-40px_rgba(201,162,75,.4)]"
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2 }}>
      <div className="absolute inset-4 rounded-t-[190px] border border-[#C9A24B]/25" />
      <span className="grid place-items-center w-20 h-20 rounded-full border border-[#C9A24B] text-[#C9A24B]"><Lock size={26} /></span>
      <h1 className="mt-8 font-display text-4xl text-[#F6EFE0]">Our Enterprise</h1>
      <p className="mt-4 font-accent italic text-xl text-[#E4C87F]">Founders, builders and investors, generation after generation.</p>
      <p className="mt-6 text-sm text-[#F6EFE0]/85">Enter family code to view our businesses</p>
      <Link to="/gate" className="btn-gold mt-8" data-testid="enterprise-gate-link">Enter family code</Link>
    </motion.div>
  </div>
);

export const BusinessModal = ({ id, onClose, members = [] }) => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/business/${id}`).then((r) => setB(r.data)).catch(() => setB({ error: true })); }, [id]);
  const name = (mid) => members.find((m) => m.id === mid)?.name || "Family member";
  return (
    <motion.div className="fixed inset-0 z-[85] bg-[#070F0B]/90 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-lenis-prevent data-testid="business-modal">
      <motion.div className="relative w-full max-w-4xl glass rounded-2xl overflow-hidden my-8" initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }}>
        <button className="absolute top-4 right-4 z-10 lb-btn !w-9 !h-9" onClick={onClose} data-testid="business-modal-close"><X size={16} /></button>
        {!b ? <Loading /> : b.error ? <p className="p-10">Not available.</p> : (
          <>
            <img src={media(b.photo_url)} alt="Family business archive photograph" className="w-full aspect-[16/7] object-cover" />
            <div className="p-6 md:p-10 grid gap-8 md:grid-cols-2">
              <div>
                <Eyebrow>{b.sector} · {b.years}</Eyebrow>
                <h2 className="mt-3 font-display text-4xl">{b.name}</h2>
                <p className="mt-3 text-[#F6EFE0]/85">{b.description}</p>
                <dl className="mt-6 grid gap-3 text-sm">
                  {[["Legal name", b.legal_name], ["Registration", b.registration], ["Board & management", b.board], ["Locations", b.locations], ["Website", b.website], ["Contacts", b.contacts]].map(([k, v]) => v ? <div key={k}><dt className="label">{k}</dt><dd className="text-[#F6EFE0]/90">{v}</dd></div> : null)}
                </dl>
              </div>
              <div>
                <p className="label">Ownership & roles</p>
                <ul className="mt-2 space-y-2">{(b.roles || []).map((r, i) => <li key={i} className="flex justify-between border-b border-[#C9A24B]/20 pb-2 text-sm"><span>{name(r.member)}</span><span className="text-[#E4C87F]">{r.role}</span></li>)}</ul>
                <p className="label mt-8">History</p>
                <ol className="mt-2 border-l border-[#C9A24B]/40 pl-5 space-y-3">{(b.history || []).map((h, i) => <li key={i} className="text-sm"><span className="font-display text-[#C9A24B] mr-2">{h.year}</span>{h.text}</li>)}</ol>
                <div className="mt-8 grid grid-cols-3 gap-2">{(b.photos_meta || []).map((p) => <img key={p.id} src={media(p.url)} alt="Family business archive photograph" className="aspect-square object-cover rounded border border-[#C9A24B]/30" />)}</div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

const Grid = () => {
  const { data, loading } = useApi("/family/overview");
  const [open, setOpen] = useState(null);
  if (loading) return <Loading />;
  const biz = data?.businesses || [];
  const inv = data?.investments || [];
  const gov = data?.governance || [];
  const members = data?.members || [];
  const name = (mid) => members.find((m) => m.id === mid)?.name || "Family member";
  return (
    <div data-testid="enterprise-open">
      <PageHead eyebrow="Our Enterprise · Family only" title="Founders, builders and investors" line="Generation after generation." />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {biz.map((b, i) => (
            <Reveal key={b.id} delay={(i % 3) * 0.08}>
              <motion.button whileHover={{ y: -6 }} className="group text-left w-full card-gold overflow-hidden" onClick={() => setOpen(b.id)} data-testid={`business-card-${i}`}>
                <div className="aspect-[16/10] overflow-hidden"><img src={media(b.photo_url)} alt="Family business archive photograph" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" /></div>
                <div className="p-6"><Eyebrow>{b.sector} · {b.years}</Eyebrow><p className="mt-2 font-display text-2xl">{b.name}</p><p className="mt-2 text-sm text-[#F6EFE0]/80">{b.description}</p></div>
              </motion.button>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-24"><h2 className="font-display text-3xl md:text-4xl">Investing</h2></Reveal>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {inv.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.05} className="card-gold p-6" data-testid={`investment-card-${i}`}>
              <p className="font-display text-xl text-[#C9A24B]">{v.category}</p>
              <p className="mt-2 text-sm">{name(v.member)} · since {v.start_year}</p>
              <p className="mt-2 text-sm text-[#F6EFE0]/80">{v.notes}</p>
              {v.show_amount && v.amount && <p className="mt-2 text-sm">Amount: {v.amount}</p>}
              {v.show_valuation && v.valuation && <p className="text-sm">Valuation: {v.valuation}</p>}
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-24"><h2 className="font-display text-3xl md:text-4xl">Family Governance</h2></Reveal>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {gov.map((g, i) => (
            <div key={g.id} className="card-gold p-6" data-testid={`governance-item-${i}`}>
              <Eyebrow>{g.kind}</Eyebrow><p className="mt-2 font-display text-xl">{g.title}</p>
              {g.media_url ? <a href={media(g.media_url)} target="_blank" rel="noreferrer" className="sweep mt-3 inline-block text-sm text-[#E4C87F]">Open document</a> : <p className="mt-2 text-sm text-[#F6EFE0]/70">Coming soon</p>}
            </div>
          ))}
        </div>
        <Disclaimer />
      </section>
      <AnimatePresence>{open && <BusinessModal id={open} members={members} onClose={() => setOpen(null)} />}</AnimatePresence>
    </div>
  );
};

export default function Enterprise() {
  const { session } = useSite() || {};
  const { id } = useParams();
  if (id && session?.active && session.scope === "business" && session.target === id) {
    return <div className="min-h-screen pt-24"><BusinessModal id={id} onClose={() => {}} /><Disclaimer /></div>;
  }
  if (!session?.active || session.scope !== "family") return <ClosedDoor />;
  return <Grid />;
}
